/**
 * Location Service
 *
 * This service handles all location-related API calls to the Core API and utility functions.
 * All functions have been migrated from Alfred service to Core API and verified to exist.
 *
 * Available Core API Endpoints:
 * - GET /core/location - Get all organization locations
 * - PUT /core/location/add - Create new location
 * - GET /core/organization/dashboard/location/{orgId}/aggregate - Location analytics
 *
 * Additional Features:
 * - Firebase integration for legacy location data
 * - Google Maps geocoding integration
 * - Client-side search and filtering
 *
 * @author FacilityAI Team
 * @version 2.0 (Core API Migration)
 */

import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { type LocationAggregate } from "@/models/organization/dashboard-analytics/dashboard-analytics.model";
import type {
	LocationTableColumnFilters,
	LocationTableSearchResult,
} from "@/models/tables/location-table/location-table.model";
import { LocationTableSearchParams } from "@/models/tables/location-table/location-table.model";
import { auth, db } from "@/services/auth-service";
import { DB_PATH } from "@constants/db";
import { doc, getDoc } from "firebase/firestore";

/**
 * Utility function to convert JSON objects to base64 encoded strings
 * Used for sending complex filter objects as URL parameters
 *
 * @param {any} json - JSON object to encode
 * @returns {string} Base64 encoded string
 *
 * @example
 * ```typescript
 * const filters = { status: 'active', type: 'retail' };
 * const encoded = jsonToBase64(filters);
 * ```
 */
export const jsonToBase64 = (json: any) => {
	return btoa(encodeURIComponent(JSON.stringify(json)));
};

/**
 * Fetches all locations for the current user's organization
 *
 * @returns {Promise<Array>} Array of location objects with basic information
 * @throws {Error} When API call fails or user lacks permissions
 *
 * @example
 * ```typescript
 * const locations = await getOrganizationLocations();
 * console.log(`Found ${locations.length} locations`);
 * ```
 */
export const getOrganizationLocations = async () => {
	try {
		const url = `${VITE_CORE_API_URL}/core/location`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json().catch(() => ({}));
			throw new Error(
				`Failed to fetch all of the organization's locations: ${errorDetails.message || "Unknown error"}`,
			);
		}
		const result = await response.json();
		return result.data; // Core API wraps response in { success: true, data: locations }
	} catch (exception: any) {
		throw new Error(exception.message || exception);
	}
};

/**
 * Fetches a single location by ID from the Core API
 *
 * @param {string|number} id - Location ID to fetch
 * @returns {Promise<Object>} Location object with detailed information
 * @throws {Error} When location not found or API call fails
 *
 * @example
 * ```typescript
 * const location = await getLocation(123);
 * console.log(location.name, location.address1);
 * ```
 */
export const getLocation = async (id: string | number): Promise<any> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/location/${id}`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status === 404) {
			throw new Error("Location not found");
		}

		if (response.status !== 200) {
			const errorDetails = await response.json().catch(() => ({}));
			throw new Error(
				`Failed to fetch location: ${errorDetails.message || "Unknown error"}`,
			);
		}

		const result = await response.json();
		return result.data; // Core API wraps response in { success: true, data: location }
	} catch (exception: any) {
		console.error(`Failed to fetch location ${id}:`, exception);
		throw new Error(exception.message || exception);
	}
};

/**
 * Legacy function to fetch location data from Firebase
 *
 * @deprecated This function uses Firebase directly and should be replaced with Core API calls when possible
 * @param {string} organization - Organization ID
 * @param {string} id - Location ID
 * @returns {Promise<DocumentSnapshot>} Firebase document snapshot
 */
export const getLocationOld = async (organization: string, id: string) => {
	const locSnap = await getDoc(
		doc(db, DB_PATH.ORGANIZATIONS, organization, DB_PATH.LOCATIONS, id),
	);
	return locSnap;
};

////////////////////////////////////////////////////////////
// Location Analytics & Aggregation
////////////////////////////////////////////////////////////

/**
 * Fetches aggregated location data with health scores for dashboard analytics
 *
 * @param {number} organizationId - Organization ID to fetch data for
 * @returns {Promise<LocationAggregate[]>} Array of location objects with health score data
 * @throws {Error} When API call fails or organization not found
 *
 * @example
 * ```typescript
 * const locationData = await getLocationAggregate(123);
 * const avgHealthScore = locationData.reduce((sum, loc) => sum + loc.healthScore, 0) / locationData.length;
 * ```
 */
export const getLocationAggregate = async (
	organizationId: number,
): Promise<LocationAggregate[]> => {
	try {
		const url = `${VITE_CORE_API_URL}${CORE_URL}/location/${organizationId}/aggregate`;

		const token = await auth?.currentUser?.getIdToken();
		if (!token) {
			console.error("No authentication token available");
			throw new Error("Authentication required");
		}

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			throw new Error(
				`Failed to fetch location aggregate: ${response.statusText}`,
			);
		}

		const data = await response.json();
		return data;
	} catch (error) {
		console.error("Error fetching location aggregate:", error);
		// Return empty array to prevent null errors
		return [];
	}
};

/**
 * Creates a new location for the current user's organization
 *
 * @param {Object} body - Location data object containing:
 *   - name: string (required)
 *   - address1: string (required)
 *   - address2?: string
 *   - city: string (required)
 *   - state: string (required)
 *   - zip: string (required)
 *   - country?: string
 *   - google_address?: string
 *   - latitude?: number
 *   - longitude?: number
 *   - store_number?: string
 * @returns {Promise<Object>} Created location object
 * @throws {Error} When required fields are missing or creation fails
 *
 * @example
 * ```typescript
 * const location = await addLocation({
 *   name: "Main Store",
 *   address1: "123 Main St",
 *   city: "New York",
 *   state: "NY",
 *   zip: "10001"
 * });
 * ```
 */
export const addLocation = async (body: any): Promise<any> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/location/add`;
		console.log("Sending location data:", body);

		const response = await fetch(url, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify(body),
		});

		if (response.status !== 200) {
			const errorData = await response.json();
			console.error("Location add error response:", errorData);
			throw new Error(`Failed to add location: ${JSON.stringify(errorData)}`);
		}
		return await response.json();
	} catch (exception: any) {
		console.error("Location add error:", exception);
		throw new Error(`Failed to add location: ${exception.message}`);
	}
};

/**
 * Searches and filters locations with pagination and sorting
 *
 * Currently implements client-side filtering as a temporary solution.
 * TODO: Implement server-side search when Core API endpoint becomes available.
 *
 * @param {string|undefined} searchValue - Text to search for in location names/addresses
 * @param {number} offset - Number of records to skip (for pagination)
 * @param {number} pageSize - Number of records to return
 * @param {string} sort - Field name to sort by
 * @param {string} sort_direction - 'ASC' or 'DESC'
 * @param {LocationTableColumnFilters} columnFilters - Additional filters to apply
 * @returns {Promise<LocationTableSearchResult>} Search results with pagination info
 * @throws {Error} When search fails or API is unavailable
 *
 * @example
 * ```typescript
 * const results = await searchLocations("store", 0, 10, "name", "ASC", {});
 * console.log(`Found ${results.count} locations, showing ${results.results.length}`);
 * ```
 */
export const searchLocations = async (
	searchValue: string | undefined,
	offset: number,
	pageSize: number,
	sort: string,
	sort_direction: string,
	columnFilters: LocationTableColumnFilters,
): Promise<LocationTableSearchResult> => {
	try {
		const searchParams: LocationTableSearchParams = {
			limit: pageSize,
			sort,
			sort_direction,
			offset,
		};

		// Only add search_value if it's a non-empty string
		if (searchValue?.trim()) {
			searchParams.search_value = searchValue.trim();
		}

		const params = new URLSearchParams(
			Object.entries(searchParams).map(([key, value]) => [
				key,
				value.toString(),
			]),
		);

		if (Object.keys(columnFilters).length > 0) {
			params.append("column_filter", jsonToBase64(columnFilters));
		}

		// Use Core API for location search (simplified version)
		const url = `${VITE_CORE_API_URL}/core/location`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to search for locations: ${errorDetails.message || "Unknown error"}`,
			);
		}

		const responseJson = await response.json();
		const locations = responseJson.data || [];

		// Client-side filtering and pagination (temporary solution)
		let filteredLocations = locations;

		// Apply search filter
		if (searchValue?.trim()) {
			const searchLower = searchValue.toLowerCase();
			filteredLocations = filteredLocations.filter(
				(location: any) =>
					location.name?.toLowerCase().includes(searchLower) ||
					location.store_number?.toLowerCase().includes(searchLower) ||
					location.address1?.toLowerCase().includes(searchLower) ||
					location.city?.toLowerCase().includes(searchLower) ||
					location.state?.toLowerCase().includes(searchLower),
			);
		}

		// Apply sorting
		if (sort) {
			filteredLocations.sort((a: any, b: any) => {
				const aVal = a[sort] || "";
				const bVal = b[sort] || "";
				const comparison = aVal.toString().localeCompare(bVal.toString());
				return sort_direction === "DESC" ? -comparison : comparison;
			});
		}

		// Apply pagination
		const total = filteredLocations.length;
		const paginatedResults = filteredLocations.slice(offset, offset + pageSize);

		return {
			results: paginatedResults,
			count: total,
		};
	} catch (exception: any) {
		throw new Error(exception.message || "Failed to search locations");
	}
};

/**
 * Geocodes an address using Google Maps API to get latitude/longitude coordinates
 *
 * @param {Object} address - Address object containing:
 *   - google_address: string - Full address string to geocode
 * @returns {Promise<Object>} Google Maps API response with geocoding results
 * @throws {Error} When geocoding fails or API key is invalid
 *
 * @example
 * ```typescript
 * const result = await getLocationGeoLocation({
 *   google_address: "123 Main St, New York, NY 10001"
 * });
 * const { lat, lng } = result.results[0].geometry.location;
 * ```
 */
export const getLocationGeoLocation = async (address: {
	google_address: any;
}) => {
	try {
		const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
		const response = await fetch(
			`https://maps.googleapis.com/maps/api/geocode/json?address=${address.google_address}&key=${apiKey}`,
		);
		return await response.json();
	} catch (exception: any) {
		throw new Error(exception);
	}
};

/**
 * Updates an existing location with new data
 *
 * @param {string|number} id - Location ID to update
 * @param {boolean} is_firebase_id - Whether the ID is a Firebase ID (legacy compatibility)
 * @param {Object} body - Update data object containing fields to update
 * @returns {Promise<Object>} Updated location object
 * @throws {Error} When update fails or location not found
 *
 * @example
 * ```typescript
 * await updateLocation(123, false, {
 *   matterport: "abc123",
 *   iguide: "def456"
 * });
 * ```
 */
export const updateLocation = async (
	id: string | number,
	is_firebase_id: boolean,
	body: any,
): Promise<any> => {
	try {
		// For now, ignore is_firebase_id since we're using Core API with numeric IDs
		const url = `${VITE_CORE_API_URL}/core/location/${id}`;

		const response = await fetch(url, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify(body),
		});

		if (response.status !== 200) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(
				`Failed to update location: ${errorData.message || "Unknown error"}`,
			);
		}

		const result = await response.json();
		return result.data; // Core API wraps response in { success: true, data: location }
	} catch (exception: any) {
		console.error("Location update error:", exception);
		throw new Error(`Failed to update location: ${exception.message}`);
	}
};

/**
 * Deletes a location by ID
 * Only succeeds if the location has zero submissions and zero assets
 *
 * @param {string|number} id - Location ID to delete
 * @returns {Promise<Object>} Result object with success status and message
 * @throws {Error} When delete fails or location has dependencies
 *
 * @example
 * ```typescript
 * try {
 *   const result = await deleteLocation(123);
 *   if (result.success) {
 *     console.log("Location deleted successfully");
 *   }
 * } catch (error) {
 *   console.error("Cannot delete:", error.message);
 * }
 * ```
 */
export const deleteLocation = async (
	id: string | number,
): Promise<{ success: boolean; message: string }> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/location/${id}`;

		const response = await fetch(url, {
			method: "DELETE",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		const result = await response.json();

		if (response.status !== 200) {
			return {
				success: false,
				message: result.message || "Failed to delete location",
			};
		}

		return {
			success: true,
			message: result.message || "Location deleted successfully",
		};
	} catch (exception: any) {
		console.error("Location delete error:", exception);
		throw new Error(`Failed to delete location: ${exception.message}`);
	}
};

/**
 * Restores a soft-deleted location by ID
 *
 * @param {string|number} id - Location ID to restore
 * @returns {Promise<Object>} Result object with success status and message
 * @throws {Error} When undelete fails
 */
export const undeleteLocation = async (
	id: string | number,
): Promise<{ success: boolean; message: string }> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/location/${id}/undelete`;

		const response = await fetch(url, {
			method: "PATCH",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		const result = await response.json();

		if (response.status !== 200) {
			return {
				success: false,
				message: result.message || "Failed to undelete location",
			};
		}

		return {
			success: true,
			message: result.message || "Location restored successfully",
		};
	} catch (exception: any) {
		console.error("Location undelete error:", exception);
		throw new Error(`Failed to undelete location: ${exception.message}`);
	}
};

/**
 * Fetches tickets associated with a location (STUB FUNCTION)
 *
 * @param {string|number} locationId - Location ID to get tickets for
 * @returns {Promise<Array>} Array of ticket objects (currently returns empty array)
 * @throws {Error} When API call fails
 *
 * @deprecated Core API does not currently support ticket endpoints
 * @todo Implement when Core API adds ticket support
 *
 * @example
 * ```typescript
 * const tickets = await getLocationTickets(123);
 * console.log(`Found ${tickets.length} tickets`);
 * ```
 */
export const getLocationTickets = async (
	locationId: string | number,
): Promise<any[]> => {
	console.warn(
		`getLocationTickets(${locationId}): Core API endpoint not implemented, returning empty array`,
	);
	// TODO: Implement when Core API supports tickets
	// const url = `${VITE_CORE_API_URL}/core/location/${locationId}/tickets`;
	// ... implementation
	return [];
};

/**
 * Save a location import record for tracking
 */
export const saveImportRecord = async (data: {
	file_name: string;
	total_rows: number;
	success_count: number;
	failed_count: number;
	errors: string[];
}): Promise<any> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/location-import`;
		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify(data),
		});
		return await response.json();
	} catch (error: any) {
		console.error("Error saving import record:", error);
		throw error;
	}
};

/**
 * Get location import history for the current organization
 */
export const getImportHistory = async (): Promise<any[]> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/location-import`;
		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});
		const result = await response.json();
		return result.data || [];
	} catch (error: any) {
		console.error("Error fetching import history:", error);
		return [];
	}
};
